import {
  CaseFindingPresentationType,
  DdOverviewDd,
  DiseaseCategoryType,
  GraphData,
  PathOptions,
  QualifiedFeature,
  QualifiedFeatureFragment,
  QualifiedFeatureInput,
  SortType,
} from "fe-common/graphql";
import { useEffect, useMemo, useState } from "react";
import { useDaoContext } from "./DaoContext";
import {
  DiseaseViewData,
  MFindingGroupType,
  rebuildEpidemiologySection,
  reshapeCategories,
} from "./MFindingGroup";
import { DefaultSort, sortFindingGroup } from "./util";
import { Values } from "shared";

export function usePathGraph(
  source: QualifiedFeatureFragment,
  target: QualifiedFeatureFragment,
  filters: QualifiedFeatureInput[] = null,
  addMsg?: (m) => void,
) {
  let dao = useDaoContext();

  let [loading, setLoading] = useState(false);

  let [graph, setGraph] = useState<GraphData>(null);

  addMsg = addMsg || console.log;

  useEffect(() => {
    if (source == null || target == null) return;

    setLoading(true);

    addMsg("Getting Graph");

    dao
      .getPathGraph(source, target, filters || [])
      .then((graph) => {
        setLoading(false);
        addMsg("Graph Received");
        setGraph(graph);
        addMsg("Graph is Ready");
      })
      .catch((e) => addMsg(e.toString()));
  }, [source, target]);

  return {
    loading,
    graph,
  };
}

export function usePatientGraph(
  source: any,
  _target: Partial<Record<CaseFindingPresentationType, QualifiedFeature[]>>,
  addMsg?: (m) => void,
) {
  const target = useMemo(() => ({ ..._target }), [_target]);

  let dao = useDaoContext();

  let [loading, setLoading] = useState(false);

  let [data, setData] = useState<{ graph: GraphData }>(null);

  let [inGraph, setInGraph] = useState<QualifiedFeatureFragment[]>([]);

  addMsg = addMsg || console.log;

  useEffect(() => {
    if (source == null || target == null || loading) return;

    Object.keys(target).forEach((g) => {
      target[g] = target[g].filter((q) => {
        if (!q || q.negate || q.skip) return false;
        // skip normal
        return q.mandatory?.value?.id !== Values.NORMAL;
      });
    });
    setLoading((currentlyLoading) => {
      if (currentlyLoading) return true;

      addMsg("Getting Graph");
      dao
        .getGraphWithFeatures(source, target)
        .then((data) => {
          setLoading(false);
          addMsg("Graph Received");

          //fix graph and compute not-in-graph
          if (data && data.graph) {
            let nodes = data.graph.nodes.filter((n) => !n.isConnected);
            data.graph.nodes = nodes;

            let inGraph = Object.values(target)
              .flat()
              .filter((t) =>
                nodes.some((n) => (n as any).sourceQF?.id == t.id),
              );
            setInGraph(inGraph);
          }
          setData(data);

          addMsg("Ready");
        })
        .catch((e) => addMsg(e.toString()))
        .finally(() => {
          setLoading(false);
        });
      return true;
    });
  }, [source, target]);

  return {
    loading,
    data,
    inGraph,
  };
}

export function useDDOverview(
  target: QualifiedFeatureFragment[],
  consider: QualifiedFeatureFragment[],
  addMsg?: (m) => void,
): {
  loading: boolean;
  data: DdOverviewDd;
  inGraph: QualifiedFeatureFragment[];
} {
  let dao = useDaoContext();
  let [loading, setLoading] = useState(false);
  let [data, setData] = useState<DdOverviewDd>(null);
  let [inGraph, setInGraph] = useState<QualifiedFeatureFragment[]>([]);
  addMsg = addMsg || console.log;

  useEffect(() => {
    if (target == null || target.length == 0 || consider == null) {
      setData(null);
      return;
    }
    setLoading(true);
    dao
      .getDDOverview(target, consider, { depth: 3, limit: 20 })
      .then((data) => {
        // fix up data
        data.clinicalReasoning?.sort(
          (a, b) => b.liftWithDependentFindings - a.liftWithDependentFindings,
        );

        setLoading(false);
        setData(data);
        addMsg("Ready");
      })
      .catch((e) => addMsg(e.toString()));
  }, [target, consider]);
  return {
    loading,
    data,
    inGraph,
  };
}

export function useDiseaseOverview(diseaseid: string, addMsg: (m) => void) {
  let [loading, setLoading] = useState(false);
  let [dvO, setDvO] = useState(null as any);
  let dao = useDaoContext();

  useEffect(() => {
    if (diseaseid == null) return;
    setLoading(true);
    setDvO(null);
    addMsg("Loading...");
    dao
      .getOverview(diseaseid)
      .then((dvO) => {
        setLoading(false);
        addMsg("Received");
        setDvO(dvO);
        addMsg("Ready");
      })
      .catch((e) => addMsg(e.toString()));
  }, [diseaseid]);

  return dvO;
}

export function useDiseaseViewData(
  diseaseid: string,
  addMsg: (m) => void,
  filters: QualifiedFeatureInput[] = null,
) {
  let [loading, setLoading] = useState(false);
  let [dv, setDV] = useState(null as DiseaseViewData);
  let dao = useDaoContext();
  filters = filters || [];
  useEffect(() => {
    if (diseaseid == null) return;
    setLoading(true);
    setDV(null);
    addMsg("Loading...");
    dao
      .getDV(diseaseid, filters)
      .then((dv) => {
        setLoading(false);
        addMsg("Received");
        let categories = reshapeCategories(
          dv.disease,
          dv.prevalence,
          dv.prevalenceDescription,
          dv.incidence,
          JSON.parse(dv.incidenceDescription),
          dv.categories,
        );

        if (categories[DiseaseCategoryType.Population]) {
          categories[
            DiseaseCategoryType.Population
          ] = rebuildEpidemiologySection(
            categories[DiseaseCategoryType.Population],
            dv.prevalence,
            dv.prevalenceDescription,
            dv.incidence,
            JSON.parse(dv.incidenceDescription),
          );
        }

        // strip multi-level groups and sort
        Object.keys(categories).forEach((c) => {
          categories[c] = removeGroups(categories[c]);
          DefaultSort[c] &&
            categories[c].sort(
              sortFindingGroup(DefaultSort[c] || SortType.Prob),
            );
        });

        setDV({ ...dv, categories });
        addMsg("Ready");
      })
      .catch((e) => addMsg(e.toString()));
  }, [diseaseid, filters?.[0], filters?.[1]]); // clearly there must be a better way - need dependency on filters - but dependency size must be constant
  return {
    loading,
    dv,
  };
}

function removeGroups(items: any) {
  return items.flatMap((v) =>
    v.type == MFindingGroupType.MULTI_LEVEL_GROUP
      ? removeGroups(v.children)
      : [v],
  );
}
